/**
 * Smart Delete — browser-side object removal via the MI-GAN ONNX
 * inpainting model.
 *
 * Why MI-GAN
 * ----------
 *   MI-GAN is a mobile-targeted inpainting GAN (~80 MB) that uses only
 *   standard convolutions. Concretely, that means:
 *     • Small download — fast first-load, friendly to mobile bandwidth.
 *     • All ops are supported by onnxruntime-web's WebGPU backend, so we
 *       get sub-second inference on any GPU-accelerated browser.
 *     • CPU/WASM fallback also runs in a few seconds rather than tens.
 *
 * Architecture
 * ------------
 *   • The runtime (`onnxruntime-web/webgpu`) and the model weights are
 *     loaded on first call only. Both are cached for the rest of the
 *     session.
 *   • The model is fetched from a HuggingFace mirror; subsequent sessions
 *     hit the browser HTTP cache and skip the wire transfer.
 *   • The "pipeline_v2" export bakes preprocessing (uint8→float, crop +
 *     resize to 512², normalization) and postprocessing (resize back,
 *     blend, →uint8) into the graph, so this file just shuffles uint8
 *     pixels in and out — no float math, no [-1,1] juggling.
 */

import type { InferenceSession, Tensor } from 'onnxruntime-web';

// MI-GAN's official "pipeline" ONNX export — preprocessing and post-
// processing are inside the graph, so callers feed uint8 image + mask
// and get a uint8 image back at the same resolution.
const MODEL_URL =
  'https://huggingface.co/andraniksargsyan/migan/resolve/main/migan_pipeline_v2.onnx';
const TILE_SIZE = 512;

type OrtModule = typeof import('onnxruntime-web/webgpu');

let runtimePromise: Promise<OrtModule> | null = null;
let sessionPromise: Promise<InferenceSession> | null = null;

/**
 * Dynamically import the WebGPU build of onnxruntime-web. The default
 * `onnxruntime-web` entry omits JSEP (the WebAssembly module that bridges
 * the runtime to WebGPU) — so importing it produces "Failed to initialize
 * JSEP" errors and silently falls back to WASM.
 *
 * Cached after the first call.
 */
function loadRuntime(): Promise<OrtModule> {
  if (!runtimePromise) {
    runtimePromise = import('onnxruntime-web/webgpu');
  }
  return runtimePromise;
}

/** Fetch the MI-GAN weights and create an InferenceSession. Cached. */
async function getSession(onProgress?: (pct: number) => void): Promise<InferenceSession> {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const ort = await loadRuntime();
      // Stream the model so we can report download progress to the UI
      // banner. Cached aggressively by the browser after the first hit.
      const response = await fetch(MODEL_URL, { cache: 'force-cache' });
      if (!response.ok || !response.body) {
        throw new Error(`Failed to download AI model (${response.status})`);
      }
      const total = Number(response.headers.get('content-length') ?? 0);
      const chunks: Uint8Array[] = [];
      let received = 0;
      const reader = response.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          received += value.byteLength;
          if (total > 0) onProgress?.(Math.round((received / total) * 100));
        }
      }
      const bytes = new Uint8Array(received);
      let offset = 0;
      for (const c of chunks) {
        bytes.set(c, offset);
        offset += c.byteLength;
      }
      // Prefer WebGPU; fall back to WASM if the browser doesn't ship
      // WebGPU or the device denies it.
      return ort.InferenceSession.create(bytes, {
        executionProviders: ['webgpu', 'wasm'],
        graphOptimizationLevel: 'all',
      });
    })().catch((err) => {
      // Reset the cached promise on failure so the next click retries.
      sessionPromise = null;
      throw err;
    });
  }
  return sessionPromise;
}

/**
 * Run MI-GAN inpainting on a (source canvas, binary mask) pair, returning
 * a canvas the same size as the inputs with the masked region filled in.
 *
 *   • `source`: the image to fill from (RGB, alpha is ignored).
 *   • `mask`: same dimensions; non-zero alpha = "inpaint here", zero =
 *     "keep this pixel".
 *
 * Caller is responsible for placing the result back on the editor's
 * paint surface — the result is not auto-composited because the editor's
 * baking logic already handles per-layer offset, marquee clipping, etc.
 */
export async function runSmartDelete(
  source: HTMLCanvasElement,
  mask: HTMLCanvasElement,
  onProgress?: (
    status: { kind: 'loading-model'; progressPct: number } | { kind: 'running' },
  ) => void,
): Promise<HTMLCanvasElement> {
  const w = source.width;
  const h = source.height;
  if (w === 0 || h === 0 || mask.width !== w || mask.height !== h) {
    throw new Error('runSmartDelete: source and mask must share dimensions.');
  }

  const ort = await loadRuntime();
  const session = await getSession((pct) =>
    onProgress?.({ kind: 'loading-model', progressPct: pct }),
  );
  onProgress?.({ kind: 'running' });

  // Resize source + mask to the model's tile size. The pipeline supports
  // arbitrary resolution in theory (it does its own crop+resize inside
  // the graph), but every public reference implementation feeds 512² and
  // it's the size the model was trained on — so we keep that bound.
  const tileSrc = document.createElement('canvas');
  tileSrc.width = TILE_SIZE;
  tileSrc.height = TILE_SIZE;
  const srcCtx = tileSrc.getContext('2d');
  if (!srcCtx) throw new Error('runSmartDelete: 2D context unavailable.');
  srcCtx.drawImage(source, 0, 0, w, h, 0, 0, TILE_SIZE, TILE_SIZE);

  const tileMask = document.createElement('canvas');
  tileMask.width = TILE_SIZE;
  tileMask.height = TILE_SIZE;
  const maskCtx = tileMask.getContext('2d');
  if (!maskCtx) throw new Error('runSmartDelete: 2D context unavailable.');
  maskCtx.drawImage(mask, 0, 0, w, h, 0, 0, TILE_SIZE, TILE_SIZE);

  // Build MI-GAN's inputs as uint8 NCHW tensors:
  //   • image: [1, 3, H, W], plain RGB bytes (no normalization — the
  //     pipeline graph handles it internally).
  //   • mask:  [1, 1, H, W], 255 = keep, 0 = inpaint.
  // Our editor's mask convention is the inverse (alpha>0 = inpaint), so
  // we invert during the copy.
  const srcData = srcCtx.getImageData(0, 0, TILE_SIZE, TILE_SIZE).data;
  const maskData = maskCtx.getImageData(0, 0, TILE_SIZE, TILE_SIZE).data;
  const pixelCount = TILE_SIZE * TILE_SIZE;
  const imageBytes = new Uint8Array(3 * pixelCount);
  const maskBytes = new Uint8Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    imageBytes[i] = srcData[i * 4]; // R
    imageBytes[pixelCount + i] = srcData[i * 4 + 1]; // G
    imageBytes[2 * pixelCount + i] = srcData[i * 4 + 2]; // B
    maskBytes[i] = maskData[i * 4 + 3] > 0 ? 0 : 255;
  }

  const inputNames = session.inputNames;
  if (inputNames.length < 2) {
    throw new Error('Smart Delete: unexpected MI-GAN input shape.');
  }
  const feeds: Record<string, Tensor> = {
    [inputNames[0]]: new ort.Tensor('uint8', imageBytes, [1, 3, TILE_SIZE, TILE_SIZE]),
    [inputNames[1]]: new ort.Tensor('uint8', maskBytes, [1, 1, TILE_SIZE, TILE_SIZE]),
  };

  // Yield to the browser so React can commit + paint the "AI Smart
  // Deleting" overlay before WASM (or WebGPU initialization) blocks the
  // main thread. A single rAF schedules at the next paint; a second rAF
  // ensures the paint actually completed (the first frame may land in
  // the same tick as our resolve). Two rAFs are equivalent in practice
  // to the previous rAF + setTimeout(50) but avoid the magic delay.
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  let result: Record<string, Tensor>;
  try {
    result = await session.run(feeds);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Smart Delete inference failed: ${message}`);
  }
  // The pipeline returns a single uint8 image tensor (NCHW). Different
  // exports name it differently; grab the first output.
  const outputName = session.outputNames[0];
  const out = result[outputName];
  if (!out || out.dims.length !== 4) {
    throw new Error('Smart Delete: unexpected model output shape.');
  }
  const outData = out.data as Uint8Array;

  // Convert tensor → ImageData on a tile-sized canvas, then resize to
  // the original (w, h). MI-GAN's pipeline returns NCHW uint8 RGB.
  const outH = out.dims[2] ?? TILE_SIZE;
  const outW = out.dims[3] ?? TILE_SIZE;
  const outPixels = outH * outW;
  const tileOut = document.createElement('canvas');
  tileOut.width = outW;
  tileOut.height = outH;
  const tileOutCtx = tileOut.getContext('2d');
  if (!tileOutCtx) throw new Error('runSmartDelete: 2D context unavailable.');
  const outImg = tileOutCtx.createImageData(outW, outH);
  for (let i = 0; i < outPixels; i++) {
    outImg.data[i * 4] = outData[i];
    outImg.data[i * 4 + 1] = outData[outPixels + i];
    outImg.data[i * 4 + 2] = outData[2 * outPixels + i];
    outImg.data[i * 4 + 3] = 255;
  }
  tileOutCtx.putImageData(outImg, 0, 0);

  const out2 = document.createElement('canvas');
  out2.width = w;
  out2.height = h;
  const out2Ctx = out2.getContext('2d');
  if (!out2Ctx) throw new Error('runSmartDelete: 2D context unavailable.');
  out2Ctx.drawImage(tileOut, 0, 0, outW, outH, 0, 0, w, h);
  return out2;
}
